import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";

/**
 * Physical counts and warehouse transfers.
 *
 * A count sheet belongs to one warehouse. Each line freezes what the ledger
 * expected when it was added (or refreshed); posting corrects the ledger by
 * counted minus expected, so stock moved during the count is kept rather than
 * counted twice. Wire names match the Java `@JsonProperty`s.
 */

export const STOCK_COUNT_STATUSES = ["OPEN", "POSTED", "CANCELLED"] as const;
export type StockCountStatus = (typeof STOCK_COUNT_STATUSES)[number];

export interface StockCountDTO {
  id: number;
  number: number;
  warehouse_id: number;
  warehouse_code?: string | null;
  /** Changed only by posting or cancelling. */
  status: StockCountStatus;
  name: string;
  count_date: string | null;
  note: string | null;
  /** The COUNT document posting made; set exactly when POSTED. */
  stock_document_id?: number | null;
  created_by_account_id?: number | null;
  posted_by_account_id?: number | null;
  posted_at?: string | null;
  line_count?: number;
  counted_count?: number;
  variance_count?: number;
}

export interface StockCountLineDTO {
  id: number;
  stock_count_id: number;
  part_id: number;
  part_number?: string | null;
  part_name?: string | null;
  stock_unit_code?: string | null;
  storage_location?: string | null;
  /** Frozen when the line was added or last refreshed. */
  expected_quantity?: number;
  /** Null until counted. */
  counted_quantity: number | null;
  /** Counted minus expected; null until counted. */
  variance?: number | null;
  /** The ledger balance now. */
  current_quantity?: number | null;
  counted_at?: string | null;
  counted_by_account_id?: number | null;
  note: string | null;
}

export interface StockCountFillRequest {
  part_group_id: number | null;
  /** Also parts the ledger holds none of — for a wall-to-wall count. */
  include_zero: boolean;
}

export interface StockCountPostRequest {
  /** Take every uncounted line as found empty. Otherwise uncounted lines are skipped. */
  zero_uncounted: boolean;
  note: string | null;
}

export interface StockCountPostResult {
  stock_document_id: number;
  movements_posted: number;
  lines_matching: number;
  lines_skipped: number;
}

export interface StockTransferRequest {
  source_warehouse_id: number;
  destination_warehouse_id: number;
  document_date: string | null;
  reference: string | null;
  note: string | null;
  lines: Array<{ part_id: number; quantity: number }>;
}

export interface StockTransferResult {
  stock_document_id: number;
  lines: Array<{
    part_number: string;
    quantity: number;
    source_balance_after: number;
    destination_balance_after: number;
  }>;
}

export class StockCountClient extends ResourceClient<StockCountDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/stock-count");
  }

  fill(id: number, request: StockCountFillRequest): Promise<StockCountDTO> {
    return this.http.post<StockCountDTO>(`${this.path}/${String(id)}/fill`, request);
  }

  refresh(id: number): Promise<StockCountDTO> {
    return this.http.post<StockCountDTO>(`${this.path}/${String(id)}/refresh`, {});
  }

  post(id: number, request: StockCountPostRequest): Promise<StockCountPostResult> {
    return this.http.post<StockCountPostResult>(`${this.path}/${String(id)}/post`, request);
  }

  cancel(id: number): Promise<StockCountDTO> {
    return this.http.post<StockCountDTO>(`${this.path}/${String(id)}/cancel`, {});
  }
}

export class StockCountLineClient extends ResourceClient<StockCountLineDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/stock-count-line");
  }
}

export class StockTransferClient {
  constructor(private readonly http: HttpClient) {}

  transfer(request: StockTransferRequest): Promise<StockTransferResult> {
    return this.http.post<StockTransferResult>("/manufacturing/stock/transfer", request);
  }
}
